<script setup lang="ts">
import { RouterView, useRoute, useRouter } from "vue-router";
import { ref } from "vue";
import SidebarNav from "@/components/SidebarNav.vue";
import { useMenuStore } from "@/stores/menu";
import { companyGuard } from "@/api/companyGuard";
import { saveUiVersion } from "@/api/menu";

const sidebarOpen = ref(false);
const menu = useMenuStore();

// The logo always acts: from another page it goes home, on the home page itself it reloads the data.
const route = useRoute();
const router = useRouter();
function goHome() {
    sidebarOpen.value = false;
    if (route.path === "/financial-data") { window.location.reload(); return; }
    router.push("/financial-data");
}

function reload() { window.location.assign("/v2/app/"); }

const switching = ref(false);
async function switchToV1() {
    switching.value = true;
    try {
        const res = await saveUiVersion("V1");
        window.location.assign(res.redirect);
    } catch {
        switching.value = false;
        alert("Preklop ni uspel. Poskusite znova.");
    }
}
</script>

<template>
    <div class="min-h-screen flex bg-gray-50 text-brand-dark">

        <!-- Sidebar (desktop) -->
        <aside v-if="!menu.setupNeeded" class="hidden md:flex md:flex-col w-64 bg-brand-dark text-white">
            <!-- The logo leads home: the financial data page. -->
            <a href="/v2/app/financial-data" @click.prevent="goHome" aria-label="Domov: finančni podatki" class="h-14 md:h-20 flex items-center px-6 border-b border-white/10 flex-shrink-0 hover:bg-white/5 transition-colors">
                <svg viewBox="0 0 100 80" class="w-9 h-9 fill-white mr-2 flex-shrink-0">
                    <polygon points="18,0 85,0 72,21 5,21" />
                    <polygon points="12,28 50,28 50,49 0,49" />
                    <polygon points="5,56 72,56 59,77 -8,77" />
                </svg>
                <span class="text-xl font-extrabold tracking-widest uppercase">Podjetnik</span>
            </a>

            <SidebarNav class="flex-1 min-h-0" />

            <div class="p-3 border-t border-white/10 flex-shrink-0">
                <RouterLink to="/nastavitve" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5 transition-colors">
                    <i data-lucide="settings" class="w-4 h-4"></i>
                    Nastavitve
                </RouterLink>
                <RouterLink v-if="menu.hasSubscription" to="/narocnina" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5 transition-colors">
                    <i data-lucide="credit-card" class="w-4 h-4"></i>
                    Naročnina
                </RouterLink>
                <a href="/v2/logoff" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5 transition-colors">
                    <i data-lucide="log-out" class="w-4 h-4"></i>
                    Odjava
                </a>
            </div>
        </aside>

        <!-- Mobile sidebar (overlay) -->
        <div v-if="sidebarOpen" class="md:hidden fixed inset-0 z-50">
            <div class="absolute inset-0 bg-brand-dark/40 backdrop-blur-sm" @click="sidebarOpen = false"></div>
            <aside class="absolute left-0 top-0 bottom-0 w-72 bg-brand-dark text-white flex flex-col">
                <div class="h-14 md:h-20 flex items-center px-6 border-b border-white/10 justify-between flex-shrink-0">
                    <a href="/v2/app/financial-data" @click.prevent="goHome" aria-label="Domov: finančni podatki" class="text-xl font-extrabold tracking-widest uppercase">Podjetnik</a>
                    <button @click="sidebarOpen = false" class="p-2 text-white/60 hover:text-white" aria-label="Zapri meni">
                        <i data-lucide="x" class="w-5 h-5"></i>
                    </button>
                </div>

                <SidebarNav class="flex-1 min-h-0" @navigate="sidebarOpen = false" />

                <div class="p-3 border-t border-white/10 flex-shrink-0">
                    <RouterLink to="/nastavitve" @click="sidebarOpen = false" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5">
                        <i data-lucide="settings" class="w-4 h-4"></i>
                        Nastavitve
                    </RouterLink>
                    <RouterLink v-if="menu.hasSubscription" to="/narocnina" @click="sidebarOpen = false" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5">
                        <i data-lucide="credit-card" class="w-4 h-4"></i>
                        Naročnina
                    </RouterLink>
                    <a href="/v2/logoff" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/5">
                        <i data-lucide="log-out" class="w-4 h-4"></i>
                        Odjava
                    </a>
                </div>
            </aside>
        </div>

        <!-- Main content area -->
        <div class="flex-1 flex flex-col min-w-0">

            <!-- "Poglej kot ...": shown for the whole preview, the one way back to the real account -->
            <div
                v-if="menu.viewAs.active"
                class="bg-red-700 text-white text-sm px-4 py-2 flex flex-wrap items-center justify-center gap-3"
            >
                <span>Gledaš kot: <strong>{{ menu.viewAs.name }}</strong> · samo ogled, spreminjanje podatkov je izklopljeno</span>
                <button
                    @click="menu.switchViewAs(null)"
                    class="bg-white text-red-700 font-bold rounded px-3 py-1 hover:bg-red-50"
                >
                    Nazaj na svoj račun
                </button>
            </div>

            <!-- Top bar -->
            <header class="h-14 md:h-20 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 flex items-center justify-between sticky top-0 z-40 shadow-sm">
                <button v-if="!menu.setupNeeded" @click="sidebarOpen = true" class="md:hidden p-2 text-gray-600" aria-label="Odpri meni">
                    <i data-lucide="menu" class="w-5 h-5"></i>
                </button>
                <div class="flex-1"></div>
                <div class="flex items-center gap-4 text-sm">
                    <span v-if="menu.company" class="text-gray-700 flex items-baseline gap-2" title="Aktivno podjetje (COMPANY_ID)">
                        <strong class="font-semibold">{{ menu.company.name }}</strong>
                        <span class="text-gray-400 tabular-nums">#{{ menu.company.id }}</span>
                    </span>
                    <!-- The sidebar (with the logout link) is hidden while the first-time setup is open -->
                    <a v-if="menu.setupNeeded" href="/v2/logoff" class="text-gray-500 hover:text-brand-dark transition-colors flex items-center gap-2">
                        <i data-lucide="log-out" class="w-4 h-4"></i>
                        Odjava
                    </a>
                    <button
                        v-if="menu.canSwitchUi"
                        @click="switchToV1"
                        :disabled="switching"
                        class="text-gray-500 hover:text-brand-dark transition-colors flex items-center gap-2 disabled:opacity-50"
                        title="Vrni se na klasični vmesnik"
                    >
                        <i data-lucide="undo-2" class="w-4 h-4"></i>
                        Nazaj na V1
                    </button>
                    <a v-if="menu.canSwitchCompany" href="/v2/izbira-podjetja" class="text-gray-500 hover:text-brand-dark transition-colors flex items-center gap-2">
                        <i data-lucide="building-2" class="w-4 h-4"></i>
                        Zamenjaj podjetje
                    </a>
                </div>
            </header>

            <!-- Routed page content -->
            <main class="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
                <RouterView />
            </main>
        </div>

        <!-- The session's company was changed in another browser window -->
        <div
            v-if="companyGuard.changedTo"
            class="fixed inset-0 z-[100] flex items-center justify-center bg-brand-dark/80 backdrop-blur-sm p-4"
            role="alertdialog" aria-modal="true" aria-labelledby="company-changed-title"
        >
            <div class="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
                <h2 id="company-changed-title" class="text-xl font-bold text-brand-dark">Podjetje je bilo zamenjano v drugem oknu</h2>
                <p class="mt-3 text-sm text-gray-600 leading-relaxed">
                    To okno prikazuje
                    <strong class="text-brand-dark">{{ companyGuard.expected?.name }}</strong>,
                    prijava pa je zdaj na podjetju
                    <strong class="text-brand-dark">{{ companyGuard.changedTo.name }}</strong>.
                    Da ne bi spreminjali podatkov napačnega podjetja, naložite okno znova.
                </p>
                <button
                    @click="reload"
                    class="mt-6 w-full py-3 px-4 rounded-md bg-brand-blue text-white text-sm font-bold hover:bg-[#3d83b5] transition-colors"
                >
                    Naloži {{ companyGuard.changedTo.name }}
                </button>
            </div>
        </div>
    </div>
</template>
